import { Preferences } from "@Enums/index";
import useUserPreferences from "@hooks/common/user/use-user-preferences";
import useI18n from "@i18n";
import { Alert, Button, Skeleton, Stack } from "@mui/material";
import { ChangeEvent } from "react";
import SettingsSwitch from "../toggles/settings-switch";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { AnalyticsEvents } from "@Enums/index";

interface IProfilePreferencesTab {
  memberPreferences: any;
  isLoading?: boolean;
}

const PreferencesSkeleton = () => (
  <Stack spacing={1.5}>
    <Skeleton variant="rounded" width="100%" height={48} />
    <Skeleton variant="rounded" width="100%" height={48} />
  </Stack>
);

const ProfilePreferencesTab = ({ memberPreferences, isLoading }: IProfilePreferencesTab) => {
  const { i18n } = useI18n();
  const { preferences, isPending: preferencesLoading, error, refetch, updatePreferences, updating, updateError } = useUserPreferences();
  const leaderboardVisibility = !!preferences?.[Preferences.leaderboard_visibility];
  const leaderboardAnonymity = !!preferences?.[Preferences.leaderboard_anonymity];

  const { track } = useAnalytics();

  const handleLeaderboardChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    updatePreference(Preferences.leaderboard_visibility, checked);
    track(AnalyticsEvents.leaderboard_status_change, { value: checked });
  };

  const handleAnonymityChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    updatePreference(Preferences.leaderboard_anonymity, checked);
    track(AnalyticsEvents.leaderboard_anonymity_change, { value: checked });
  };

  const updatePreference = (id: Preferences, value: boolean) => {
    updatePreferences([{ id, value }]);
  };

  if (isLoading || preferencesLoading) {
    return <PreferencesSkeleton />;
  }

  return (
    <Stack spacing={1.5}>
      {(error || updateError) && (
        <Alert
          severity="error"
          action={
            error ? (
              <Button color="inherit" onClick={() => void refetch()}>
                {i18n("button.retry")}
              </Button>
            ) : undefined
          }
        >
          {(error || updateError)?.message}
        </Alert>
      )}
      <SettingsSwitch
        name="leaderboard"
        label={i18n("settings.profile.preferences.join-leaderboards")}
        disabled={updating || !preferences}
        checked={leaderboardVisibility}
        onChange={handleLeaderboardChange}
      />
      <SettingsSwitch
        name="anonymity"
        label={i18n("settings.profile.preferences.anonymous-ranking")}
        disabled={updating || !preferences}
        checked={leaderboardAnonymity}
        onChange={handleAnonymityChange}
      />
    </Stack>
  );
};

export default ProfilePreferencesTab;
